import { useEffect, useRef, useState } from "react";

import { t } from "i18next";
import invariant from "ts-invariant";

import type {
  FullUserCourse,
  HealthFormType,
  PutUserCoursePayload,
  UserCourseExercise,
  UserCourseLesson,
  UserCourseLibraryExercise,
  UserCourseLibraryExercises,
  UserCourseTemplateExercise,
  UserCourseTrimmedExercise,
} from "api/schemas/UserCourse";
import { useProfileContext } from "contexts/ProfileContext";
import { ConfirmationPopup } from "shared/atoms/ConfirmationPopup";

import { NoExercisesConfirmDialog } from "../NoExercisesConfirmDialog";

import { Drawer } from "./Drawer";
import { ExerciseEditPopup } from "./ExerciseEditPopup";
import { ExercisePicker } from "./ExercisePicker";
import { ExercisesList } from "./ExercisesList";
import { LessonPicker } from "./LessonPicker";
import { LessonsList } from "./LessonsList";
import { UserCourseEditorSummary } from "./UserCourseEditorSummary";

const checkNoExerciseNextWeekWarning = (exercises: UserCourseExercise[], patientWeek: number) => {
  // Determine the highest introduction_protocol_week
  const maxIntroductionWeek = Math.max(
    ...exercises.map(exercise => exercise.introduction_protocol_week),
    0 // Default to 0 if there are no exercises
  );

  // Set a finite `finalWeek` which provides a buffer for calculations
  const finalWeek = maxIntroductionWeek + 52; // Adding a buffer

  // Initialize a Set to track weeks covered by exercises
  const coveredWeeks = new Set<number>();

  // Mark weeks as covered for each exercise's active range
  exercises.forEach(exercise => {
    const endWeek = exercise.end_protocol_week ?? finalWeek; // Use end_protocol_week if defined, else treat as indefinite
    for (let week = exercise.introduction_protocol_week; week <= endWeek; week += 1) {
      coveredWeeks.add(week);
    }
  });

  // Find missing weeks in the range from current patient's week to `finalWeek`
  const weeksWithoutExercises: number[] = [];
  const patientNextWeek = patientWeek + 1;
  for (let week = patientNextWeek; week <= finalWeek; week += 1) {
    if (!coveredWeeks.has(week)) {
      weeksWithoutExercises.push(week);
    }
  }

  if (weeksWithoutExercises.length > 0) {
    return weeksWithoutExercises;
  }
  return [];
};

const convertTemplateExercisesToUsercourseExercises = (
  exercises: UserCourseTemplateExercise[]
): UserCourseExercise[] => {
  const deepCopy: UserCourseTemplateExercise[] = JSON.parse(JSON.stringify(exercises));
  return deepCopy.map(e => ({
    current_level: 1,
    active: false,
    ...e,
  }));
};

const createCoursePayload = ({
  exercises,
  lessons,
  exercisesPerDay,
  functionalTest,
  healthFormType,
}: {
  exercises: UserCourseTrimmedExercise[];
  lessons: UserCourseLesson[];
  exercisesPerDay: number;
  functionalTest: boolean;
  healthFormType: HealthFormType;
}) => {
  const cleanedExercises = exercises.map(e => {
    const { id, introduction_protocol_week, current_level, end_protocol_week } = e;
    return {
      exercise_id: Number(id),
      introduction_protocol_week,
      ...(current_level ? { level_recommendation: current_level } : {}),
      end_protocol_week,
    };
  });

  const cleanedLessons = lessons.map(e => {
    return {
      lesson_id: Number(e.id),
    };
  });

  return {
    exercises: cleanedExercises,
    lessons: cleanedLessons,
    max_exercises_per_day: exercisesPerDay,
    functional_test: functionalTest,
    health_form_type: healthFormType,
  };
};

export type UserCourseView = "exercises" | "lessons";
export interface MissingExercises {
  weeks: number[];
  overridden: boolean;
}

export const UserCourseEditor = ({
  initialData,
  onSaveUserCourse,
  showNameInput,
  currentProtocolWeek,
  showActiveSelector,
  initialName,
  exercisePickerVisible = false,
  onExercisePickerClosed,
  onCourseChanged,
  saveButtonLabel,
  canEditLessons,
  onActivate,
  saveButtonDisabled,
  onNewCourse,
}: {
  initialData?: FullUserCourse;
  onSaveUserCourse: (course: PutUserCoursePayload, name: string) => void;
  showNameInput?: boolean;
  currentProtocolWeek?: number | undefined;
  showActiveSelector?: boolean;
  initialName?: string;
  exercisePickerVisible?: boolean;
  onExercisePickerClosed?: () => void;
  onCourseChanged?: (course: PutUserCoursePayload) => void;
  saveButtonLabel: string;
  canEditLessons?: boolean;
  onActivate?: (course: PutUserCoursePayload) => void;
  saveButtonDisabled?: boolean;
  onNewCourse?: () => void;
}) => {
  const [exercisesPerDay, setExercisesPerDay] = useState<number>(1);
  const [showExercisePicker, setShowExercisePicker] = useState(exercisePickerVisible);
  const [showLessonPicker, setShowLessonPicker] = useState(false);
  const [userExercises, setUserExercises] = useState<UserCourseExercise[]>(
    initialData?.exercises ? convertTemplateExercisesToUsercourseExercises(initialData.exercises) || [] : []
  );
  const [userLessons, setUserLessons] = useState<UserCourseLesson[]>(initialData?.lessons || []);
  const [selectedExercise, setSelectedExercise] = useState<UserCourseExercise | undefined>();
  const [newlyAddedExercises, setNewlyAddedExercises] = useState<number[]>([]);
  const [newlyAddedLessons, setNewlyAddedLessons] = useState<number[]>([]);
  const [functionalTest, setFunctionalTest] = useState<boolean>(initialData?.functional_test || true);
  const [healthFormType, setHealthFormType] = useState<HealthFormType>(initialData?.health_form_type || "generic");
  const [view, setView] = useState<UserCourseView>("exercises");
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const previousStateRef = useRef<UserCourseExercise[]>([]);
  const [missingExercises, setMissingExercises] = useState<MissingExercises>({
    weeks: [],
    overridden: false,
  });
  const [name, setName] = useState<string>(initialName || "");

  const { profile } = useProfileContext();
  invariant(profile);

  useEffect(() => {
    if (!initialData) {
      setUserExercises([]);
      setUserLessons([]);
      setName("");
      setFunctionalTest(true);
      setHealthFormType("generic");
    }

    if (initialData) {
      if (initialData.exercises) {
        setUserExercises(convertTemplateExercisesToUsercourseExercises(initialData.exercises));
      }
      if (initialData.lessons) {
        setUserLessons(initialData.lessons);
      } else {
        setUserLessons([]);
      }
      if (initialData.max_exercises_per_day) {
        changeExercisesPerDay(initialData.max_exercises_per_day);
      }
      setFunctionalTest(initialData.functional_test);
      setHealthFormType(initialData.health_form_type);
    }
  }, [initialData]);

  useEffect(() => {
    if (initialName) {
      setName(initialName);
    } else {
      setName("");
    }
  }, [initialName]);

  useEffect(() => {
    if (currentProtocolWeek !== undefined) {
      if (userExercises.length > 0 && missingExercises.overridden === false) {
        const weeksWithoutExercises = checkNoExerciseNextWeekWarning(userExercises, currentProtocolWeek);
        setMissingExercises(prevState => ({ ...prevState, weeks: weeksWithoutExercises }));
      }
    }
  }, [userExercises, currentProtocolWeek, missingExercises.overridden, currentProtocolWeek]);

  const openExercisePicker = () => {
    setShowExercisePicker(true);
  };

  useEffect(() => {
    setShowExercisePicker(exercisePickerVisible);
  }, [exercisePickerVisible]);

  useEffect(() => {
    if (!showExercisePicker) {
      onExercisePickerClosed?.();
    }
  }, [showExercisePicker]);

  useEffect(() => {
    if (onCourseChanged) {
      const course: PutUserCoursePayload = createCoursePayload({
        exercises: userExercises,
        lessons: userLessons,
        exercisesPerDay,
        functionalTest,
        healthFormType: healthFormType || "generic",
      });
      onCourseChanged(course);
    }
  }, [userExercises, userLessons, exercisesPerDay, functionalTest, healthFormType, onCourseChanged]);

  useEffect(() => {
    if (selectedExercise && userExercises) {
      const updatedExercise = userExercises.find(e => e.id === selectedExercise.id);
      if (updatedExercise) {
        setSelectedExercise(updatedExercise);
      }
    }
  }, [selectedExercise, userExercises]);

  const openLessonPicker = () => {
    setShowLessonPicker(true);
  };

  const addExercises = (addedExercises: UserCourseLibraryExercises) => {
    setUserExercises((state: UserCourseExercise[]) => {
      const newState = [...state];

      // only add new ones so we dont reset week, level etc.
      const newExercises = addedExercises.filter(ae => {
        const match = !!state.find(ue => ue.id === ae.exercise_id);
        return !match;
      });

      newExercises.forEach((exercise: UserCourseLibraryExercise) => {
        newState.push({
          active: false,
          current_level: 1,
          end_protocol_week: null,
          id: exercise.exercise_id,
          introduction_protocol_week: currentProtocolWeek || 1,
          levels: JSON.parse(JSON.stringify(exercise.levels)),
          premium: exercise.premium,
          target_joints: exercise.target_joints,
        });
      });

      setNewlyAddedExercises(newExercises.map(exercise => exercise.exercise_id));

      return newState;
    });
    setShowExercisePicker(false);
    onExercisePickerClosed?.();
  };

  const addLessons = (addedLessons: UserCourseLesson[]) => {
    setUserLessons((state: UserCourseLesson[]) => {
      // remove exercises not included in the new ones
      const newState = [...state].filter(ul => {
        const match = addedLessons.find(al => al.id === ul.id);
        return match;
      });

      // only add new ones so we dont reset week, level etc.
      const newLessons = addedLessons.filter(al => {
        const match = !!state.find(ul => ul.id === al.id);
        return !match;
      });

      newLessons.forEach((lesson: UserCourseLesson) => {
        newState.push(lesson);
      });

      setNewlyAddedLessons(newLessons.map(lesson => lesson.id));
      setTimeout(() => setNewlyAddedLessons([]), 5000);

      return newState;
    });
    setShowLessonPicker(false);
  };

  const deleteExercise = (id: number) => {
    setUserExercises((state: UserCourseExercise[]) => {
      previousStateRef.current = state.map(exercise => ({ ...exercise }));
      return state.filter(exercise => exercise.id !== id);
    });
  };

  const changeLevel = (id: number, level: number) => {
    setUserExercises((state: UserCourseExercise[]) => {
      const newState = [...state];
      const index = newState.findIndex(exercise => exercise.id === id);
      newState[index].current_level = level;
      return newState;
    });
  };

  const changeWeek = (id: number, week: number) => {
    setUserExercises((state: UserCourseExercise[]) => {
      previousStateRef.current = state.map(exercise => ({ ...exercise }));
      const newState = [...state];
      const index = newState.findIndex(exercise => exercise.id === id);
      if (index !== -1) {
        newState[index] = {
          ...newState[index],
          introduction_protocol_week: week,
        };
      }
      return newState;
    });
  };

  const changeEndWeek = (id: number, week: number | null) => {
    setUserExercises((state: UserCourseExercise[]) => {
      previousStateRef.current = state.map(exercise => ({ ...exercise }));
      const newState = [...state];
      const index = newState.findIndex(exercise => exercise.id === id);
      if (index !== -1) {
        newState[index] = {
          ...newState[index],
          end_protocol_week: week,
        };
      }
      return newState;
    });
  };

  const changeExercisesPerDay = (n: number) => {
    setExercisesPerDay(n);
  };

  const changeName = (_name: string) => {
    setName(_name);
  };

  const deleteLesson = (id: number) => {
    setUserLessons((state: UserCourseLesson[]) => {
      return state.filter(lesson => lesson.id !== id);
    });
  };

  const clearExercises = () => {
    setUserExercises([]);
  };

  const saveUserCourse = async (_name?: string) => {
    const course = createCoursePayload({
      exercises: userExercises,
      lessons: userLessons,
      exercisesPerDay,
      functionalTest,
      healthFormType: healthFormType || "generic",
    });

    onSaveUserCourse(course, _name || "");
  };

  const activate = async () => {
    if (!onActivate) {
      return;
    }
    const course = createCoursePayload({
      exercises: userExercises,
      lessons: userLessons,
      exercisesPerDay,
      functionalTest,
      healthFormType: healthFormType || "generic",
    });

    onActivate(course);
  };

  const clickExercise = (id: number) => {
    const match: UserCourseExercise | undefined = userExercises.find(e => e.id === id);
    if (match) {
      setSelectedExercise(match);
    }
  };

  const changeFunctionalTest = (b: boolean) => {
    setFunctionalTest(b);
  };

  const revertExerciseChange = () => {
    if (previousStateRef.current.length) {
      setUserExercises(previousStateRef.current);
    }
  };

  return (
    <>
      <UserCourseEditorSummary
        onSaveUserCourse={() => {
          saveUserCourse(name);
        }}
        onActivate={
          onActivate
            ? () => {
                activate();
              }
            : undefined
        }
        saveButtonLabel={saveButtonLabel}
        saveButtonDisabled={!!saveButtonDisabled}
        onChangeView={v => setView(v)}
        view={view}
        exercisesPerDay={exercisesPerDay}
        onChangeExercisesPerDay={changeExercisesPerDay}
        functionalTest={functionalTest}
        onChangeFunctionalTest={changeFunctionalTest}
        healthFormType={healthFormType}
        name={name}
        showNameInput={showNameInput}
        onNameChange={changeName}
        nbrOfExercises={userExercises.length}
        nbrOfLessons={userLessons.length}
        onNewCourse={onNewCourse}
      />

      {view === "exercises" && (
        <>
          <ExercisesList
            showActiveSelector={!!showActiveSelector}
            exercises={userExercises}
            onOpenExercisePicker={openExercisePicker}
            onClickExercise={clickExercise}
            onDeleteExercise={deleteExercise}
            currentProtocolWeek={currentProtocolWeek}
            onChangeLevel={changeLevel}
            onChangeWeek={changeWeek}
            onChangeEndWeek={changeEndWeek}
            newlyAddedExercises={newlyAddedExercises}
            onClearExercises={() => setShowClearConfirm(true)}
          />
        </>
      )}

      {view === "lessons" && (
        <>
          <LessonsList
            lessons={userLessons}
            onDeleteLesson={deleteLesson}
            onSetLessons={lessons => {
              setUserLessons(lessons);
            }}
            newlyAddedLessons={newlyAddedLessons}
            onOpenLessonPicker={openLessonPicker}
            editable={!!canEditLessons}
          />
        </>
      )}

      {selectedExercise && (
        <ExerciseEditPopup
          exercise={selectedExercise}
          currentProtocolWeek={currentProtocolWeek || 0}
          onChangeEndWeek={changeEndWeek}
          onChangeLevel={changeLevel}
          onChangeWeek={changeWeek}
          onClose={() => setSelectedExercise(undefined)}
        />
      )}

      <Drawer open={showExercisePicker} onClose={() => setShowExercisePicker(false)} backgroundColor="#f8f8f8">
        <ExercisePicker
          initialSelectedIds={userExercises.map(e => e.id)}
          onAddExercises={addExercises}
          onClose={() => {
            setShowExercisePicker(false);
          }}
        />
      </Drawer>
      <Drawer open={showLessonPicker} onClose={() => setShowLessonPicker(false)} backgroundColor="#f8f8f8">
        <LessonPicker
          initialSelectedIds={userLessons.map(e => e.id)}
          onAddLessons={addLessons}
          onClose={() => {
            setShowLessonPicker(false);
          }}
        />
      </Drawer>
      {showClearConfirm && (
        <ConfirmationPopup
          isOpen={showClearConfirm}
          title={t("usercourse.remove_all_exercises.header")}
          onClickOutside={() => setShowClearConfirm(false)}
          onConfirm={() => {
            clearExercises();
            setShowClearConfirm(false);
          }}
          onCancel={() => setShowClearConfirm(false)}
        />
      )}
      {missingExercises.weeks.length > 0 && (
        <NoExercisesConfirmDialog
          weeksMissingExercises={missingExercises.weeks}
          canRevert={!!previousStateRef.current.length}
          onRevert={revertExerciseChange}
          onIgnoreMissingExercises={() => setMissingExercises({ weeks: [], overridden: true })}
        />
      )}
    </>
  );
};
